/**
 * Tillar. Birlamchi — o'zbek (lotin). Qo'shimcha: rus, ingliz, xitoy (soddalashtirilgan).
 *
 * Tarjima BOSQICHMA-BOSQICH qo'shiladi, shuning uchun `uz` dan boshqa hammasi IXTIYORIY:
 * tarjima yo'q bo'lsa `tr()` o'zbekchasini qaytaradi va interfeys hech qachon bo'sh qolmaydi.
 */
export const LOCALES = ['uz', 'ru', 'en', 'zh'] as const;
export type Locale = (typeof LOCALES)[number];
export const DEFAULT_LOCALE: Locale = 'uz';

/** Til tanlagichi uchun — har til O'Z tilida yoziladi (xalqaro odat). */
export const LOCALE_LABELS: Record<Locale, { native: string; short: string }> = {
  uz: { native: "O'zbekcha", short: 'UZ' },
  ru: { native: 'Русский', short: 'RU' },
  en: { native: 'English', short: 'EN' },
  zh: { native: '中文', short: '中文' },
};

/** HTML `lang` va `Intl` uchun to'liq teg. */
export const LOCALE_TAGS: Record<Locale, string> = {
  uz: 'uz-UZ',
  ru: 'ru-RU',
  en: 'en-US',
  zh: 'zh-CN',
};

export function isLocale(v: unknown): v is Locale {
  return typeof v === 'string' && (LOCALES as readonly string[]).includes(v);
}

/**
 * Ko'p tilli matn (ma'lumotnomalar, statuslar, ruxsatlar, tovar nomlari).
 * `uz` majburiy — u zaxira qiymat; qolganlari tarjima qilinishi bilan to'ldiriladi.
 */
export interface L10n {
  uz: string;
  ru?: string;
  en?: string;
  zh?: string;
}

/** Tanlangan tildagi matn; tarjima yo'q bo'lsa — o'zbekchasi. */
export function tr(text: L10n | null | undefined, locale: Locale = DEFAULT_LOCALE): string {
  if (!text) return '';
  return text[locale] || text.uz;
}

/**
 * Bazadagi ustunlardan ko'p tilli matn yig'ish: `pick(row, 'title', locale)` →
 * `title_zh` bo'lsa o'sha, bo'lmasa `title`. Tovar/kategoriya nomlari shu tarzda saqlanadi.
 */
export function pickField<T extends Record<string, unknown>>(row: T, base: string, locale: Locale): string {
  if (locale !== 'uz') {
    const suffixed = row[`${base}${locale[0]!.toUpperCase()}${locale.slice(1)}`];
    if (typeof suffixed === 'string' && suffixed.trim()) return suffixed;
  }
  const fallback = row[base];
  return typeof fallback === 'string' ? fallback : '';
}

/** Til qo'shimchasi: uz -> "Uz", ru -> "Ru" ... (ustun nomlarini yasash uchun). */
export const LOCALE_SUFFIX: Record<Locale, string> = { uz: 'Uz', ru: 'Ru', en: 'En', zh: 'Zh' };

/**
 * O'zbekchasi ham qo'shimchali bo'lgan ustunlar uchun (`labelUz`, `labelRu`, `labelEn`, `labelZh`).
 * Tanlangan til bo'sh bo'lsa — o'zbekchasi qaytadi.
 */
export function pickSuffixed(row: object, base: string, locale: Locale): string {
  const r = row as Record<string, unknown>;
  const v = r[`${base}${LOCALE_SUFFIX[locale]}`];
  if (typeof v === 'string' && v.trim()) return v;
  const uz = r[`${base}Uz`];
  return typeof uz === 'string' ? uz : '';
}

/**
 * `Accept-Language` sarlavhasidan tilni aniqlash (API va bot uchun).
 * Masalan: "zh-CN,zh;q=0.9,en;q=0.8" → "zh".
 */
export function localeFromHeader(header: string | null | undefined): Locale | null {
  if (!header) return null;
  const parts = header
    .split(',')
    .map((p) => {
      const [tag, q] = p.trim().split(';q=');
      return { tag: (tag ?? '').trim().toLowerCase(), q: q ? Number(q) : 1 };
    })
    .filter((p) => p.tag)
    .sort((a, b) => b.q - a.q);
  for (const p of parts) {
    const base = p.tag.split('-')[0]!;
    if (isLocale(base)) return base;
  }
  return null;
}
